import {createApi} from '@reduxjs/toolkit/query/react';
import {axiosBaseQuery} from './api-utils';
import {
  CardOrderRequest,
  CardPreInstruction,
  CardPreInstructionDetailsRequest,
  CardPreInstructionResponse,
  CardPreInstructionsRequest,
  CanceledCardPreInstructionRequest,
  RestartCardPreInstructionRequest, CosiDataResponse
} from '../@types/CardSubscriptionTypes';
import {CardType} from '../@types/cardTypes';

export interface CardLimits {
  lower: number;
  upper: number;
}

export interface CardHolderEligibilityResponse {
  eligible: boolean;
}

export interface CardOrderEligibilityResponse {
  eligible: boolean;
}

export interface CardLimitResponse {
  limits: CardLimits | null;
}

export interface CompanyInformationResponse {
  normalizedCompanyName: string;
  companyPostalAddress: string;
}

export interface CardOrderEligibilityRequest {
  primaryContract: string;
}

export interface CardLimitRequest {
  primaryContract: string;
  type: CardType;
}

export interface CardHolderEligibilityRequest {
  thirdPartyId: string;
}

export interface CompanyInformationRequest {
  contractReference: string;
}

export interface ContractUsersRequest {
  contractReference: string;
}

export interface ContractEligibleUsersRequest {
  contractReference: string;
  primaryContract: string;
}

type Sex = 'MALE' | 'FEMALE';

export interface ContractUser {
  ssn: string;
  displayName: string;
  lastName?: string;
  firstName?: string;
  currentUser: boolean;
  sex?: Sex;
  birthDate?: Date;
}

export interface ContractUserResponse {
  users: ContractUser[];
}

export interface PrimaryContractBusinessLineRequest {
  primaryContract: string;
}

export interface PrimaryContractBusinessLineResponse {
  businessLineCode: string;
}

const URL_API = '/business_webctrsub/rest/card-order';

export const cardSubscriptionApi = createApi({
  reducerPath: 'cardOrderApi',
  baseQuery: axiosBaseQuery({
    baseUrl: URL_API
  }),
  tagTypes: ['CardOrder', 'CardHolder', 'CardLimit', 'CompanyInformation', 'PreInstructions', 'SaveHolderData'],

  endpoints: (builder) => ({
    eligibleBusinessCardOrder: builder.query<CardOrderEligibilityResponse, CardOrderEligibilityRequest>({
      query: (request) => ({
        url: '/canOrderCard',
        method: 'POST',
        data: request
      })
    }),

    eligibleCardHolder: builder.query<CardHolderEligibilityResponse, CardHolderEligibilityRequest>({
      query: (request) => ({
        url: '/canHoldCard',
        method: 'POST',
        data: request
      })
    }),

    getCardLimit: builder.query<CardLimitResponse, CardLimitRequest>({
      query: (request) => ({
        url: '/card-limit',
        method: 'POST',
        data: request
      })
    }),

    getCompanyInformation: builder.query<CompanyInformationResponse, CompanyInformationRequest>({
      query: (request) => ({
        url: '/company-information',
        method: 'POST',
        data: request
      })
    }),

    getContractUsers: builder.query<ContractUserResponse, ContractUsersRequest>({
      query: (request) => ({
        url: '/contract-users',
        method: 'POST',
        data: request
      })
    }),

    getContractEligibleUsers: builder.query<ContractUserResponse, ContractEligibleUsersRequest>({
      query: (request) => ({
        url: '/contract-users-rsc',
        method: 'POST',
        data: request
      })
    }),

    order: builder.mutation<CardPreInstruction, CardOrderRequest>({
      query: (request) => ({
        url: '/pre-instruction/create',
        method: 'POST',
        data: request
      })
    }),

    getCardPreInstructionDetails: builder.query<CardPreInstruction, CardPreInstructionDetailsRequest>({
      query: (request) => ({
        url: '/pre-instruction/details',
        method: 'POST',
        data: request
      })
    }),

    getCardPreInstructionLightDetails: builder.query<CardPreInstruction, CardPreInstructionDetailsRequest>({
      query: (request) => ({
        url: '/pre-instruction/light-details',
        method: 'POST',
        data: request
      })
    }),

    getListCardPreInstructions: builder.query<CardPreInstructionResponse, CardPreInstructionsRequest>({
      query: request => ({
        url: '/pre-instruction/list',
        method: 'POST',
        data: request
      }),
      providesTags: (result, messages, request) => [
        {type: 'PreInstructions', uuid: request}
      ]
    }),
    canceledCardPreInstruction: builder.mutation<void, CanceledCardPreInstructionRequest>({
      query: request => ({
        url: '/pre-instruction/cancel',
        method: 'POST',
        data: request
      }),
      invalidatesTags: (result, error, request) => [
        {type: 'PreInstructions'}
      ]
    }),
    restartCardPreInstruction: builder.mutation<void, RestartCardPreInstructionRequest>({
      query: request => ({
        url: '/pre-instruction/restart',
        method: 'POST',
        data: request
      }),
      invalidatesTags: (result, error, request) => [
        {type: 'PreInstructions'}
      ]
    }),

    saveHolderData: builder.mutation<CosiDataResponse, FormData>({
      query: formData => ({
        url: '/pre-instruction/save-holder-data',
        method: 'POST',
        data: formData
      })
    }),

    getPrimaryContractBusinessLine: builder.query<PrimaryContractBusinessLineResponse, PrimaryContractBusinessLineRequest>({
      query: (request) => ({
        url: '/business-line',
        method: 'POST',
        data: request
      })
    })
  })
});

export const {
  useEligibleBusinessCardOrderQuery,
  useEligibleCardHolderQuery,
  useGetCardLimitQuery,
  useGetCompanyInformationQuery,
  useGetContractUsersQuery,
  useGetContractEligibleUsersQuery,
  useOrderMutation,
  useGetCardPreInstructionDetailsQuery,
  useSaveHolderDataMutation,
  useGetPrimaryContractBusinessLineQuery,
  useGetCardPreInstructionLightDetailsQuery,
  useGetListCardPreInstructionsQuery,
  useCanceledCardPreInstructionMutation,
  useRestartCardPreInstructionMutation
} = cardSubscriptionApi;
